import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSearch,
  faBox,
  faMapMarkerAlt,
  faTruck,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faCalendarAlt,
  faUser,
  faPhone,
  faEnvelope,
  faCreditCard,
  faChevronDown,
  faChevronUp,
  faWarehouse,
  faShippingFast,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { shipmentService } from '../../services/shipmentService';
import { getCustomerTrackingStatuses, formatTrackingCode } from '../../utils/formatters';
import { paymentsService } from '../../services/paymentsService';

const inferTypeFromCode = (code) => {
  const c = String(code || '').toUpperCase();
  const idx = c.startsWith('FX') ? 2 : 0;
  const lead = c[idx];
  if (lead === '1') return 'local';
  if (lead === '2') return 'interstate';
  if (lead === '3') return 'international';
  return 'interstate';
};

const TrackShipment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [trackingNumber, setTrackingNumber] = useState(id || '');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fullDetails, setFullDetails] = useState(null);
  const [showSenderDetails, setShowSenderDetails] = useState(false);
  const [showReceiverDetails, setShowReceiverDetails] = useState(false);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);

  useEffect(() => {
    if (id) {
      setTrackingNumber(id);
      handleTrackShipment();
    }
  }, [id]);

  const handleTrackShipment = async () => {
    if (!trackingNumber.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setAnimateProgress(false);

    try {
      const resp = await shipmentService.track(trackingNumber.trim());
      const s = resp.shipment || {};
      const timeline = Array.isArray(s.statusHistory) ? s.statusHistory.map((h) => {
        let loc = h.note || h.status || 'Processing';
        const agentInfo = [h.updatedByName, h.updatedByEmail].filter(Boolean).join(' • ');
        if (agentInfo) {
          loc = loc ? `${loc} — ${agentInfo}` : agentInfo;
        }

        return {
          date: h.updatedAt ? new Date(h.updatedAt).toISOString().split('T')[0] : '',
          time: h.updatedAt ? new Date(h.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
          location: loc,
          status: h.status || 'Update',
          updatedByName: h.updatedByName || '',
          updatedByEmail: h.updatedByEmail || '',
          updatedBy: h.updatedBy || '',
          completed: true
        };
      }) : [];

      setTrackingData({
        id: formatTrackingCode(s.trackingCode || trackingNumber),
        status: s.status || 'Pending',
        estimatedDelivery: '—',
        origin: s.pickupLocation || '—',
        destination: s.receiverName ? `${s.receiverName}` : '—',
        serviceType: s.serviceType || 'interstate',
        sender: {
          name: s.senderName || '—',
          phone: s.senderNumber || '—',
          email: s.email || '—',
          address: s.senderAddress || '—'
        },
        receiver: {
          name: s.receiverName || '—',
          phone: s.receiverNumber || '—',
          email: '—',
          address: s.receiverAddress || '—'
        },
        package: {
          weight: s.weight ? `${s.weight} kg` : '—',
          dimensions: '—',
          description: s.packageType || '—',
          items: s.numberOfItems || '—'
        },
        timeline
      });

      setTimeout(() => setAnimateProgress(true), 100);

      try {
        const token = localStorage.getItem('token');
        if (token) {
          const my = await shipmentService.getMy(token);
          const list = my?.shipments || my || [];
          const match = list.find(x => (String(x.trackingCode || '').toUpperCase() === String(s.trackingCode || trackingNumber).toUpperCase()));
          if (match) {
            setFullDetails({
              shipmentId: match._id || match.id || null,
              trackingCode: formatTrackingCode(match.trackingCode || (s.trackingCode || trackingNumber)),
              email: match.email || '',
              price: typeof match.price === 'number' ? match.price : Number(match.price) || null,
              paymentType: match.paymentType || 'payNow',
              paymentStatus: match.paymentStatus || 'Pending'
            });
          }
        }
      } catch (e) {
        setFullDetails(null);
      }
    } catch (err) {
      console.error('Tracking failed', err);
      setError(err?.response?.data?.msg || 'Shipment not found. Please check your tracking number.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const list = getCustomerTrackingStatuses(trackingData?.serviceType || inferTypeFromCode(trackingNumber));
    const idx = Math.max(0, list.indexOf(status));
    const total = Math.max(1, list.length - 1);
    const pct = Math.round((idx / total) * 100);
    if (status === 'Delivered') {
      return { color: 'green', icon: faCheckCircle, bg: 'bg-green-100', text: 'text-green-700', progress: 100 };
    }
    if (status === 'Out for Delivery') {
      return { color: 'purple', icon: faShippingFast, bg: 'bg-purple-100', text: 'text-purple-700', progress: pct };
    }
    if (status === 'In Transit' || status === 'International Transit' || status === 'In Transit to Airport') {
      return { color: 'yellow', icon: faTruck, bg: 'bg-yellow-100', text: 'text-yellow-700', progress: pct };
    }
    if (status === 'Origin' || status === 'Pickup' || status === 'Shipment by Carrier' || status === 'At Departure Airport' || status === 'At Destination Airport' || status === 'Custom Clearance') {
      return { color: 'blue', icon: faBox, bg: 'bg-blue-100', text: 'text-blue-700', progress: pct };
    }
    return { color: 'gray', icon: faClock, bg: 'bg-gray-100', text: 'text-gray-700', progress: pct };
  };

  const handlePayOnline = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to make a payment.');
        return;
      }
      const email = fullDetails?.email;
      const amount = fullDetails?.price;
      const sid = fullDetails?.shipmentId;
      if (!email || !amount || !sid) {
        alert('Missing payment details. Please contact support.');
        return;
      }
      const init = await paymentsService.initialize({ email, amount, shipmentId: sid }, token);
      const authUrl = init?.data?.authorization_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error('Unable to start online payment. Please try again.');
      }
    } catch (err) {
      alert(err?.response?.data?.msg || err?.message || 'Failed to initialize payment');
    }
  };

  const statusInfo = trackingData ? getStatusInfo(trackingData.status) : null;
  const normalizeStage = (s) => {
    const k = String(s || '').toLowerCase();
    if (k.includes('delivered')) return 'Delivered';
    if (k.includes('out for delivery')) return 'Out for Delivery';
    if (k.includes('transit')) return 'In Transit';
    return 'Origin';
  };
  const stageTS = (stage) => {
    const t = trackingData?.timeline || [];
    const find = (pred) => {
      for (let i = t.length - 1; i >= 0; i--) {
        if (pred(t[i])) return t[i];
      }
      return null;
    };
    if (stage === 'Delivered') return find(x => String(x.status || '').toLowerCase().includes('delivered'));
    if (stage === 'Out for Delivery') return find(x => String(x.status || '').toLowerCase().includes('out for delivery'));
    if (stage === 'In Transit') return find(x => String(x.status || '').toLowerCase().includes('transit'));
    if (stage === 'Origin') return t[0] || null;
    return null;
  };
  const activeStage = trackingData ? normalizeStage(trackingData.status) : 'Origin';
  const stagesToShow = (() => {
    if (activeStage === 'Origin') return [{ name: 'Origin', type: 'active', icon: faWarehouse }];
    if (activeStage === 'In Transit')
      return [
        { name: 'Origin', type: 'completed', icon: faWarehouse },
        { name: 'In Transit', type: 'active', icon: faTruck }
      ];
    if (activeStage === 'Out for Delivery')
      return [
        { name: 'In Transit', type: 'completed', icon: faTruck },
        { name: 'Out for Delivery', type: 'active', icon: faShippingFast }
      ];
    return [
      { name: 'Out for Delivery', type: 'completed', icon: faShippingFast },
      { name: 'Delivered', type: 'active', icon: faCheckCircle }
    ];
  })();
  const nextStepText = (() => {
    if (activeStage === 'Origin') return 'Package will be picked up within 24 hours';
    if (activeStage === 'In Transit') return 'Package will arrive at local delivery hub';
    if (activeStage === 'Out for Delivery') return 'Courier will attempt delivery today';
    return '';
  })();
  const getFriendlyLocation = () => {
    const last = (trackingData?.timeline && trackingData.timeline.length) ? trackingData.timeline[trackingData.timeline.length - 1] : null;
    const raw = last?.location || '';
    const bad = /(payment|agent|unknown|none|n\/a|processing|status)/i.test(raw) || raw.trim().length < 3;
    if (raw && !bad && raw !== '—') return raw;
    if (activeStage === 'Origin') return 'Origin Facility';
    if (activeStage === 'In Transit') return 'In Transit to Destination';
    if (activeStage === 'Out for Delivery') return 'Local Delivery Hub';
    if (activeStage === 'Delivered') return 'Delivered to Recipient';
    return '—';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-20 md:pb-6">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Track Package</h1>
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-md p-5 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Enter Tracking Number
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="e.g., FX1xxxxx"
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTrackShipment()}
              />
            </div>
            <button
              onClick={handleTrackShipment}
              disabled={loading}
              className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all font-medium"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
            </button>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start">
              <FontAwesomeIcon icon={faTimesCircle} className="mr-2 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {trackingData && (
          <div className="space-y-5 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-md p-6 overflow-hidden">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900">Shipment Status</h2>
                <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${statusInfo.bg} ${statusInfo.text}`}>
                  {trackingData.status}
                </span>
              </div>

              <div className="relative mb-8">
                <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 rounded-full"></div>
                <div
                  className={`absolute top-8 left-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full transition-all duration-1000 ease-out ${animateProgress ? 'opacity-100' : 'opacity-0'}`}
                  style={{
                    width: animateProgress ? `${statusInfo.progress}%` : '0%',
                    transition: 'width 1.5s ease-out'
                  }}
                ></div>

                <div className="relative flex justify-center gap-12 items-start pt-2">
                  {stagesToShow.map((st, i) => {
                    const ts = stageTS(st.name);
                    const isActive = st.type === 'active';
                    const isCompleted = st.type === 'completed';
                    const bg = isActive ? 'bg-blue-500' : isCompleted ? 'bg-blue-500' : 'bg-gray-200';
                    const scale = isActive ? 'scale-110' : isCompleted ? 'scale-100' : 'scale-90';
                    const iconColor = isActive || isCompleted ? 'text-white' : 'text-gray-400';
                    const labelColor = isActive ? 'text-gray-900' : 'text-gray-700';
                    return (
                      <div key={`${st.name}-${i}`} className="flex flex-col items-center">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-2 transition-all ${bg} ${scale}`}>
                          <FontAwesomeIcon icon={st.icon} className={`text-2xl ${iconColor}`} />
                        </div>
                        <p className={`text-sm font-semibold ${labelColor} text-center`}>{st.name}</p>
                        {isCompleted && ts && (
                          <p className="text-xs text-gray-500 text-center mt-1">✓ {ts.date} • {ts.time}</p>
                        )}
                        {isActive && (
                          <p className="text-xs text-gray-500 text-center mt-1">In progress…</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Tracking ID</p>
                  <p className="font-semibold text-sm text-gray-900">{trackingData.id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Est. Delivery</p>
                  <p className="font-semibold text-sm text-gray-900">{trackingData.estimatedDelivery}</p>
                </div>
              </div>

              {nextStepText && (
                <div className="mt-4">
                  <span className="inline-block px-3 py-1.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700">
                    Next: {nextStepText}
                  </span>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Current Status Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current Status</p>
                    <p className="font-semibold text-sm text-gray-900">{trackingData.status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current Details</p>
                    <p className="font-semibold text-sm text-gray-900">{getFriendlyLocation()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                    <p className="font-semibold text-sm text-gray-900">{(trackingData.timeline && trackingData.timeline.length) ? `${trackingData.timeline[trackingData.timeline.length-1].date} • ${trackingData.timeline[trackingData.timeline.length-1].time}` : '—'}</p>
                  </div>
                </div>
              </div>

              {fullDetails && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${fullDetails.paymentStatus === 'Paid' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                          <FontAwesomeIcon icon={faCreditCard} className={fullDetails.paymentStatus === 'Paid' ? 'text-green-600' : 'text-yellow-600'} />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Payment Information</h3>
                          <p className={`text-xs font-bold uppercase tracking-wider ${fullDetails.paymentStatus === 'Paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                            {fullDetails.paymentStatus === 'Paid' ? 'Payment Confirmed' : 'Pending Payment'}
                          </p>
                        </div>
                      </div>
                      <FontAwesomeIcon
                        icon={showPaymentDetails ? faChevronUp : faChevronDown}
                        className="text-gray-400"
                      />
                    </button>

                    {showPaymentDetails && (
                      <div className="px-5 pb-5 animate-slide-down">
                        <div className="flex flex-col items-center gap-3">
                          <div className={`text-sm font-bold ${fullDetails.paymentStatus === 'Paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                            {fullDetails.paymentStatus === 'Paid' ? (
                              <><FontAwesomeIcon icon={faCheckCircle} className="mr-1.5" /> Payment Confirmed</>
                            ) : (
                              'Payment Pending'
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <button
                onClick={() => setShowSenderDetails(!showSenderDetails)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faUser} className="text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Sender Information</h3>
                </div>
                <FontAwesomeIcon
                  icon={showSenderDetails ? faChevronUp : faChevronDown}
                  className="text-gray-400"
                />
              </button>
              {showSenderDetails && (
                <div className="px-5 pb-5 space-y-3 animate-slide-down">
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faUser} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.sender.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.sender.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.sender.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <button
                onClick={() => setShowReceiverDetails(!showReceiverDetails)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faUser} className="text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Receiver Information</h3>
                </div>
                <FontAwesomeIcon
                  icon={showReceiverDetails ? faChevronUp : faChevronDown}
                  className="text-gray-400"
                />
              </button>
              {showReceiverDetails && (
                <div className="px-5 pb-5 space-y-3 animate-slide-down">
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faUser} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.receiver.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.receiver.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 mt-1 mr-3 w-4" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.receiver.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <button
                onClick={() => setShowPackageDetails(!showPackageDetails)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faBox} className="text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Package Details</h3>
                </div>
                <FontAwesomeIcon
                  icon={showPackageDetails ? faChevronUp : faChevronDown}
                  className="text-gray-400"
                />
              </button>
              {showPackageDetails && (
                <div className="px-5 pb-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Weight</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.package.weight}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Items</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.package.items}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Type</p>
                      <p className="text-sm font-medium text-gray-900">{trackingData.package.description}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {trackingData.timeline.length > 0 && (
              <div className="bg-white rounded-2xl shadow-md p-5">
                <h3 className="font-semibold text-gray-900 mb-5 flex items-center">
                  <FontAwesomeIcon icon={faClock} className="mr-2 text-gray-600" />
                  Tracking History
                </h3>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-5">
                    {trackingData.timeline.map((event, index) => (
                      <div key={index} className="relative flex items-start animate-slide-in" style={{ animationDelay: `${index * 100}ms` }}>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center z-10 ${event.completed ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                          <FontAwesomeIcon icon={getStatusInfo(event.status).icon} className="text-xs" />
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">{event.status}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                            {event.date} • {event.time}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{event.location}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-down {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 500px; }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out; }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
        .animate-slide-in { animation: slide-in 0.5s ease-out; }
      `}</style>
    </div>
  );
};

export default TrackShipment;
